import { useCallback, useEffect, useState } from "react";
import { CREDIT_CARD_LABEL, CONTRIBUTOR_INDIVIDUAL, DONATIONFREEUENCY_ONCE, DONATIONFREQUENCY_MONTHLY } from "../utilities/constants";
import { getValidUrl } from "../utilities/urlHelper";
import { browserWindow, browserDocument } from "../utilities/browser";

/**
 * This hooks is to handle global donation realated field's state management
 * And field interaction between states that needs to be used in multiple components.
 *
 * In this component fields usually related to front-end behaviour not handling the post data to server.
 */
const useDonationContext = () => {
  // These initialisers run during render, including on the server, where window does
  // not exist. Optional chaining is not enough on its own: window?.x still throws when
  // window is undeclared rather than null.
  const atDonationAsk = browserWindow()?.atDonationAsk;

  const [donationAmount, setDonationAmount] = useState(atDonationAsk?.askAmount ?? "0"); //Make sure to use it like setDonationAmount(value)
  const [overwriteStatement, setOverwriteStatement] = useState(atDonationAsk?.impactStatement ?? ""); //new
  const [overwriteAskAmount, setOverwriteAskAmount] = useState(atDonationAsk?.askAmount ?? ""); //new
  const [overwriteMonthlyAskAmount, setOverwriteMonthlyAskAmount] = useState(atDonationAsk?.monthlyAskAmount ?? ""); //new
  // const [totalDonationAmount, setTotalDonationAmount] = useState(donationAmount); // moved to useTotalDonationAmountAndProcessingCost
  // const [donationProcessingCost, setDonationProcessingCost] = useState(donationAmount.toFixed(2)); // moved to useTotalDonationAmountAndProcessingCost, original const [amountPercentage, setAmountPercentage] = useState(donationAmount);
  // const [percentage, setPercentage] = useState(donationAmount); // removed because of no usage

  const [shouldCoverDonationProcessingCost, setShouldCoverDonationProcessingCost] = useState(false); //checked, setChecked
  const [donationFixedProcessingCost, setDonationFixedProcessingCost] = useState(0);
  const [donationPercentProcessingCost, setDonationPercentProcessingCost] = useState(0); //donationPercent, setDonationPercent

  const [donationFrequency, setDonationFrequency] = useState(atDonationAsk?.donationFrequency ?? ""); // use to be called selectionTab or frequency or logicalField
  const [paymentMethod, setPaymentMethod] = useState(CREDIT_CARD_LABEL);
  // const [isApplePay, setIsApplePay] = useState(paymentMethod === APPLE_PAY_LABEL); /removed to use paymentMethod === APPLE_PAY_LABEL
  // const [isPaypal, setIsPaypal] = useState(paymentMethod === PAYPAL_LABEL); //removed to use paymentMethod === PAYPAL_LABEL
  const [contributor, setContributor] = useState(CONTRIBUTOR_INDIVIDUAL);

  const [showAddDollarAmount, setShowAddDollarAmount] = useState(true); //checkBoxView, setCheckBoxView
  // const [checked, setChecked] = useState(false); //checked, setChecked
  // const [donationPost, setDonationPost] = useState(); //donatoinPost, setdonatoinPost
  // const [isFormLoaded, setIsFormLoaded] = useState(false); //isLoad, setIsLoad //move to useFormInputsValidation.js
  // const [activeSelection, setActiveSelection] = useState(""); //activeTab, setActiveTab, logicalField, setLogicalField (updates, when selection field updates) //move to useInputsFormValidation.js

  //set up success and failure pages
  const [failurePage, setFailurePage] = useState(""); //failurePage, setfailurePage
  const [successPage, setSuccessPage] = useState(""); //successPage, setsuccessPage
  const [recurringPaymentSuccessPage, setRecurringPaymentSuccessPage] = useState("");
  const getSuccessPage = (forceAbosolute = false) => {
    if (typeof window === "undefined") return "";
    if (recurringPaymentSuccessPage && donationFrequency !== "once") {
      return getValidUrl(recurringPaymentSuccessPage, forceAbosolute);
      //return recurringPaymentSuccessPage.startsWith("/") ? recurringPaymentSuccessPage : "/" + recurringPaymentSuccessPage;
    }

    return getValidUrl(successPage, forceAbosolute);
    // const relativeUrl = successPage.startsWith("/") ? successPage : "/" + successPage;
    // return forceAbosolute ? relativeUrl : getAbsoluteUrl(relativeUrl);
  };

  const getFailurePage = (forceAbosolute = false) => {
    if (typeof window === "undefined") return "";
    return getValidUrl(failurePage, forceAbosolute);
    // const relativePath = failurePage.startsWith("/") && failurePage.startsWith() ? failurePage : "/" + failurePage;
    // return forceAbosolute ? relativePath : getAbsoluteUrl(relativePath);
  };

  // useEffect(
  //   function onPaymentMethodUpdate() {
  //     setIsPaypal(paymentMethod === PAYPAL_LABEL);
  //     setIsApplePay(paymentMethod === APPLE_PAY_LABEL);
  //   },
  //   [paymentMethod]
  // );

  const [totalDonationAmount, donationProcessingCost, donationIncreasedProcessingPercentage] = useTotalDonationAmountAndProcessingCost(
    donationAmount,
    donationFixedProcessingCost,
    donationPercentProcessingCost,
    shouldCoverDonationProcessingCost
  );

  useListenDonationAmountByElementWithIDAsOtherAmount(setDonationAmount);

  // Stable, public setters that update both React state + window cache
  const setAskAmountPublic = (value) => {
    setOverwriteAskAmount(value);
    if (typeof window !== "undefined") {
      window.atDonationAsk = window.atDonationAsk || {};
      window.atDonationAsk.askAmount = value;
    }
  }

  const setMonthlyAskAmountPublic = (value) => {
    setOverwriteMonthlyAskAmount(value);
    if (typeof window !== "undefined") {
      window.atDonationAsk = window.atDonationAsk || {};
      window.atDonationAsk.monthlyAskAmount = value;
    }
  }

  const setOtherAmountPublic = (value) => {
    setDonationAmount(value);
    if (typeof window !== "undefined") {
      window.atDonationAsk = window.atDonationAsk || {};
      window.atDonationAsk.otherAmount = value;
    }
  }

  const setImpactStatementPublic = (value) => {
    setOverwriteStatement(value || "");
    if (typeof window !== "undefined") {
      window.atDonationAsk = window.atDonationAsk || {};
      window.atDonationAsk.impactStatement = value || "";
    }
  }

  const setDonationFrequencyPublic = (value) => {
    // support both "once"/"monthly" and 0/1
    //  eslint-disable-next-line eqeqeq
    const frequency = 0 == value ? DONATIONFREEUENCY_ONCE : DONATIONFREQUENCY_MONTHLY;
    setDonationFrequency(frequency);
    if (typeof window !== "undefined") {
      window.atDonationAsk = window.atDonationAsk || {};
      window.atDonationAsk.donationFrequency = frequency;
    }
  }

  // Mount: replace stubs with real functions, drain queue, apply personalization
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Get any queued calls from early bootstrap
    const queue = Array.isArray(window.__donationQueue) ? window.__donationQueue.slice() : [];

    // Replace window functions with real implementations
    window.setAskAmount = setAskAmountPublic;
    window.setMonthlyAskAmount = setMonthlyAskAmountPublic;
    window.setOtherAmount = setOtherAmountPublic;
    window.setImpactStatement = setImpactStatementPublic;
    window.setDonationFrequency = setDonationFrequencyPublic;

    // Clear the queue
    window.__donationQueue = [];

    // Drain any calls made before React was ready (including third-party scripts)
    for (const job of queue) {
      try {
        if (job.fn === "setAskAmount") window.setAskAmount(job.value);
        if (job.fn === "setMonthlyAskAmount") window.setMonthlyAskAmount(job.value);
        if (job.fn === "setOtherAmount") window.setOtherAmount(job.value);
        if (job.fn === "setImpactStatement") window.setImpactStatement(job.value);
        if (job.fn === "setDonationFrequency") window.setDonationFrequency(job.value);
        // Note: third-party scripts can still override personalization by calling these functions
      } catch (error) {
        console.warn("Error processing queued donation API call:", error);
      }
    }

    console.log("Donation API ready. Processed", queue.length, "queued calls.");
  }, [setAskAmountPublic, setMonthlyAskAmountPublic, setOtherAmountPublic, setImpactStatementPublic, setDonationFrequencyPublic]);

  // Listen for custom update events and call all public setters
  useEffect(() => {
    const handleUpdateEvent = (event) => {
      // Update askAmount if provided in event or from window
      if (event.detail?.askAmount !== undefined) {
        setOverwriteAskAmount(event.detail.askAmount);
        window.atDonationAsk = window.atDonationAsk || {};
        window.atDonationAsk.askAmount = event.detail.askAmount;
      } else if (window?.atDonationAsk?.askAmount !== undefined) {
        setOverwriteAskAmount(window.atDonationAsk.askAmount);
      }

      // Update otherAmount if provided in event or from window
      if (event.detail?.otherAmount !== undefined) {
        setDonationAmount(event.detail.otherAmount);
        window.atDonationAsk = window.atDonationAsk || {};
        window.atDonationAsk.otherAmount = event.detail.otherAmount;
      } else if (window?.atDonationAsk?.otherAmount !== undefined) {
        setDonationAmount(window.atDonationAsk.otherAmount);
      }

      // Update impactStatement if provided in event or from window
      if (event.detail?.impactStatement !== undefined) {
        setOverwriteStatement(event.detail.impactStatement);
        window.atDonationAsk = window.atDonationAsk || {};
        window.atDonationAsk.impactStatement = event.detail.impactStatement;
      } else if (window?.atDonationAsk?.impactStatement !== undefined) {
        setOverwriteStatement(window.atDonationAsk.impactStatement);
      }

      // Update donationFrequency if provided in event or from window
      if (event.detail?.donationFrequency !== undefined) {
        setDonationFrequency(event.detail.donationFrequency);
        window.atDonationAsk = window.atDonationAsk || {};
        window.atDonationAsk.donationFrequency = event.detail.donationFrequency;
      } else if (window?.atDonationAsk?.donationFrequency !== undefined) {
        setDonationFrequency(window.atDonationAsk.donationFrequency);
      }
    };

    window.addEventListener('starlight:personalisation-update', handleUpdateEvent);

    return () => {
      window.removeEventListener('starlight:personalisation-update', handleUpdateEvent);
    };
  }, []);
 
  // run whenever activeTab is changed
  // useEffect(() => {
  //   if (isFormLoaded) {
  //     validateForm();
  //   }
  // }, [activeSelection]);

  // let donationState = {
  //   thank_you_url: () => (window ? determineSuccessPage() : ""),
  // };

  //isValidation
  // const validateForm = () => {};

  return {
    donationAmount,
    setDonationAmount,
    overwriteStatement,
    overwriteAskAmount,
    overwriteMonthlyAskAmount,

    totalDonationAmount,
    // setTotalDonationAmount,
    donationPercentProcessingCost,
    setDonationPercentProcessingCost,
    donationFixedProcessingCost,
    setDonationFixedProcessingCost,
    donationProcessingCost,
    donationIncreasedProcessingPercentage,
    // setDonationProcessingCost,

    donationFrequency,
    setDonationFrequency,
    paymentMethod,
    setPaymentMethod,
    contributor,
    setContributor,

    //donation form field interaction
    shouldCoverDonationProcessingCost,
    setShouldCoverDonationProcessingCost,
    showAddDollarAmount,
    setShowAddDollarAmount,

    //form fields interaction
    // isFormLoaded,
    // setIsFormLoaded,
    // activeSelection,
    // setActiveSelection,

    //success and failure page
    failurePage,
    setFailurePage,
    getFailurePage,
    successPage,
    setSuccessPage,
    getSuccessPage,
    recurringPaymentSuccessPage,
    setRecurringPaymentSuccessPage,
  };
};

export default useDonationContext;

//hooks

/**
   * there are external script will update this "#other-amount",
   * we need to use that input as single source of truth for value in the donation form
   * When using an external event, please make sure it triggers the "input" event, not just value change
   * 
    const otherAmountInput = document.getElementById("other-amount");
    if (otherAmountInput) {
        otherAmountInput.value = "$100"; // Update the value

        // Create and dispatch an 'input' event
        const event = new Event('input', { bubbles: true });
        otherAmountInput.dispatchEvent(event);
    }
   */
const useListenDonationAmountByElementWithIDAsOtherAmount = (setDonationAmount) => {
  // This useEffect only should run once to bind the handleInputChange function to the input element change event
  useEffect(
    function onComponentMount() {
      const otherAmountInput = document.getElementById("other-amount");
      const handleInputChange = (event) => {
        if (event.target.value) {
          const newAmount = event.target.value;
          const cleaned = newAmount.replace(/\$/g, "").trim();
          setDonationAmount(cleaned);
        }
      };

      if (otherAmountInput) {
        otherAmountInput.addEventListener("input", handleInputChange);
      }

      return () => {
        if (otherAmountInput) {
          otherAmountInput.removeEventListener("input", handleInputChange);
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    //needed to make sure the input element is loaded. Dependency arrays are evaluated
    //during render, including on the server, so document has to be checked here.
    [setDonationAmount, browserDocument()?.getElementById("other-amount") ?? null]
  );
};

// update the total donation amount and processing cost by the donation amount, fixed processing cost, percent processing cost, and shouldCoverDonationProcessingCost
const useTotalDonationAmountAndProcessingCost = (
  donationAmount,
  donationFixedProcessingCost,
  donationPercentProcessingCost,
  shouldCoverDonationProcessingCost
) => {
  const [donationProcessingCost, setDonationProcessingCost] = useState(parseFloat(donationAmount).toFixed(2));
  const [totalDonationAmount, setTotalDonationAmount] = useState(parseFloat(donationAmount).toFixed(2));
  const [donationIncreasedProcessingPercentage, setDonationIncreasedProcessingPercentage] = useState(donationProcessingCost / donationAmount);

  useEffect(() => {
    const getIncreasedPercentage = (donationAmount, donationFixedProcessingCost, donationPercentProcessingCost) => {
      if (window?.processingFeeAmount) return "0";
      if (donationFixedProcessingCost) return parseFloat(donationFixedProcessingCost / donationAmount).toString();
      if (donationPercentProcessingCost) return (parseFloat(donationPercentProcessingCost) / 100).toString();

      return "0";
    };

    const donationAmountNumber = parseFloat(donationAmount);
    const defaultFixedProcessingCost = parseFloat(donationFixedProcessingCost);
    const amountIncresed = window.processingFeeAmount || defaultFixedProcessingCost || (donationAmountNumber * donationPercentProcessingCost) / 100;
    const acturalIncreasedProcessingPercentage = getIncreasedPercentage(donationAmount, donationFixedProcessingCost, donationPercentProcessingCost);

    setTotalDonationAmount(
      shouldCoverDonationProcessingCost ? (amountIncresed + donationAmountNumber).toFixed(2) : parseFloat(donationAmount).toFixed(2)
    );
    setDonationIncreasedProcessingPercentage(acturalIncreasedProcessingPercentage);
    setDonationProcessingCost(amountIncresed.toFixed(2));
  }, [
    donationAmount,
    donationFixedProcessingCost,
    donationPercentProcessingCost,
    shouldCoverDonationProcessingCost,
    setTotalDonationAmount,
    setDonationProcessingCost,
    setDonationIncreasedProcessingPercentage,
  ]);

  return [totalDonationAmount, donationProcessingCost, donationIncreasedProcessingPercentage];
};
